import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon, FileTextIcon, FolderIcon, SearchIcon, XIcon } from 'lucide-react';
import {
  CANONICAL_DOCUMENT_TYPES,
  CANONICAL_SPECIALISATIONS,
  UNTYPED_DOCUMENT_TYPE,
  type DocumentRegistryEntry,
  specialisationLabel,
} from '@kennisbank/shared';
import { ApiError, apiGet, apiPatch } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { SortableTh, toggleSort } from '../components/SortableTh.js';
import { WijzigingsverzoekLink } from '../components/WijzigingsverzoekButton.js';
import { InlineSpecialisationsCell } from '../components/InlineSpecialisationsCell.js';
import { DocumentPreview } from '../components/DocumentPreview.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

/**
 * Practitioner browse surface for the kennisbank corpus.
 *
 * Twin to Sociale Kaart — same operator vocabulary, same chip rail
 * pattern, same canonical specialisation list. Distinct from the
 * Kennisagent chat: this surface is "I want to find a specific
 * document"; chat is "I have a question and want a synthesised answer."
 *
 * RBAC: open to every authenticated role. The kennisbank corpus is
 * platform-wide reference data — admin actions stay in /beheer/kennisbank.
 *
 * Filtering is done client-side after the initial load (server already
 * applies filters too, but a fresh fetch on every keystroke is wasteful
 * for ~30-50 docs). Free-text q is debounced via the existing search
 * input semantics.
 */

type SortColumn = 'title' | 'author' | 'documentType' | 'lastIngestedAt';

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];
const DEFAULT_PAGE_SIZE: PageSize = 50;

interface ListResponse {
  documents: DocumentRegistryEntry[];
  total: number;
  page: number;
  pageSize: number;
  facets: { authors: string[]; documentTypes: string[]; folders: FolderFacet[] };
}

/** Eén map in de boom links: ring + documenttype + aantal documenten. */
interface FolderFacet {
  ring: 'ring0' | 'ring1';
  /** Leeg = documenten zonder type; die vallen onder "Overig". */
  documentType: string;
  count: number;
}

/**
 * De twee bibliotheken in de boom. De ring IS de scheiding: Ring 0 is de
 * SUPPOHRT-interne corpus (personeelshandboek, werkinstructies) en Ring 1 de
 * klinische kennisbank. Ring 0 komt alleen mee in de facetten als de backend
 * hem voor deze rol vrijgeeft, dus de map verbergt zich vanzelf voor externen.
 */
const LIBRARIES = [
  {
    ring: 'ring0' as const,
    label: 'Werkprocessen',
    hint: 'Hoe wij werken: handboek, werkinstructies, beleid.',
  },
  {
    ring: 'ring1' as const,
    label: 'Zorginhoudelijke kennis',
    hint: 'Klinische richtlijnen, protocollen, werkboeken.',
  },
];

// Ring 2/3 = externe webbronnen die Mila gebruikt in haar antwoorden (niet
// downloadbaar zoals de Ring 1-documenten). Het paneel toont de bron-
// organisaties, niet elke gecrawlde pagina.
interface ExternalSource {
  id: string;
  label: string;
  ring: 2 | 3;
  homepage: string;
  pageCount: number;
}

export function DocumentenPage(): JSX.Element {
  const { kennisbank } = useTenantBrand();
  const [documents, setDocuments] = useState<DocumentRegistryEntry[]>([]);
  // Total is the FILTERED corpus count from the server, not the rendered
  // page slice — drives the "X van Y" footer + pagination math.
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  // Facets are the distinct author + documentType values across the WHOLE
  // corpus (server-supplied), so the filter dropdowns stay correct even
  // when the visible page only has 50 of 622 docs.
  const [facets, setFacets] = useState<ListResponse['facets']>({
    authors: [],
    documentTypes: [],
    folders: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selectedSpecs, setSelectedSpecs] = useState<Set<string>>(new Set());
  const [authorFilter, setAuthorFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [ringFilter, setRingFilter] = useState<'' | 'ring0' | 'ring1'>('');
  const [sortBy, setSortBy] = useState<SortColumn>('title');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [externalSources, setExternalSources] = useState<ExternalSource[]>([]);
  const [previewDoc, setPreviewDoc] = useState<DocumentRegistryEntry | null>(null);

  // Externe bronnen (Ring 2/3) — eenmalig laden; faalt stil (het paneel
  // verbergt zich gewoon als er niets is of de call mislukt).
  useEffect(() => {
    void apiGet<{ sources: ExternalSource[] }>('/api/v1/documents/external-sources')
      .then((res) => setExternalSources(res.sources))
      .catch(() => setExternalSources([]));
  }, []);

  // Inline editing — admins only. Non-admins see the cells as plain text;
  // suppohrt_admin gets click-to-edit on author + specialisations so they
  // can fix typos / add tags without bouncing to /beheer/kennisbank when
  // already filtered down by author or specialisation here.
  const { user } = useAuth();
  const canEdit = user?.role === 'suppohrt_admin';
  const [savingFilename, setSavingFilename] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const patchDocument = useCallback(
    async (filename: string, patch: Partial<DocumentRegistryEntry>): Promise<void> => {
      setSavingFilename(filename);
      setSaveError(null);
      try {
        const res = await apiPatch<{ document: DocumentRegistryEntry }>(
          `/api/v1/beheer/kennisbank/documents/${encodeURIComponent(filename)}`,
          patch,
        );
        // Optimistic + server-confirmed update merged into the table.
        setDocuments((cur) =>
          cur.map((d) => (d.filename === filename ? { ...d, ...res.document } : d)),
        );
      } catch (err) {
        setSaveError(
          err instanceof ApiError && err.code === 'ingest_lock_held'
            ? 'Indexering loopt nog. Probeer het zo opnieuw.'
            : err instanceof Error
              ? err.message
              : 'Bijwerken mislukt.',
        );
      } finally {
        setSavingFilename(null);
      }
    },
    [],
  );

  // Reset to page 1 whenever filters/sort/page-size change — staying on
  // page 5 when the user picks a new author would show "no results" or
  // skip past the narrowed-down hits at the top. Page-only changes don't
  // reset (the pagination buttons need to actually move the page).
  useEffect(() => {
    setPage(1);
  }, [q, selectedSpecs, authorFilter, typeFilter, ringFilter, sortBy, sortDir, pageSize]);

  // Server-driven fetch — re-fires on every filter / page / sort change.
  // 250ms debounce ONLY on the free-text `q` input (keystroke storm);
  // the dropdowns + chip clicks fire immediately because they're discrete.
  useEffect(() => {
    let cancelled = false;
    const debounceMs = q.trim().length > 0 ? 250 : 0;
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      selectedSpecs.forEach((s) => params.append('specialisation', s));
      if (authorFilter) params.set('author', authorFilter);
      if (typeFilter) params.set('documentType', typeFilter);
      if (ringFilter) params.set('ring', ringFilter);
      params.set('sortBy', sortBy);
      params.set('sortDir', sortDir);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      setLoading(true);
      setError(null);
      apiGet<ListResponse>(`/api/v1/documents?${params.toString()}`)
        .then((res) => {
          if (cancelled) return;
          setDocuments(res.documents);
          setTotal(res.total);
          setFacets({ ...res.facets, folders: res.facets.folders ?? [] });
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(err instanceof ApiError ? err.message : 'Kon documentenlijst niet laden.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, selectedSpecs, authorFilter, typeFilter, ringFilter, sortBy, sortDir, page, pageSize]);

  // Pagination math — totalPages drives the Volgende/Vorige enable state.
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, total);

  const toggleSpec = (s: string): void => {
    setSelectedSpecs((cur) => {
      const next = new Set(cur);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const clearFilters = (): void => {
    setQ('');
    setSelectedSpecs(new Set());
    setAuthorFilter('');
    setTypeFilter('');
    setRingFilter('');
  };

  // De typedropdown volgt de gekozen map: staat er een bibliotheek open, dan
  // toont hij alleen de types die in die ring voorkomen. Anders kies je een
  // type dat in deze map niet bestaat en krijg je een lege lijst zonder dat
  // ergens te zien is waarom.
  const typeOptions = ringFilter
    ? facets.folders
        .filter((f) => f.ring === ringFilter && f.documentType)
        .map((f) => f.documentType)
    : facets.documentTypes;

  // De tellingen in de boom lopen over de hele corpus, niet over de
  // gefilterde set. Zodra er een zoekterm, auteur of specialisatie actief is
  // spreken ze de lijst tegen ("Protocol 84" naast "1-3 van 3"), dus dan
  // verbergen we ze liever dan een getal te tonen dat niet klopt.
  const folderCountsMeaningful =
    q.length === 0 && selectedSpecs.size === 0 && authorFilter.length === 0;

  const hasActiveFilters =
    q.length > 0 ||
    selectedSpecs.size > 0 ||
    authorFilter.length > 0 ||
    typeFilter.length > 0 ||
    ringFilter.length > 0;

  return (
    <Layout>
      {previewDoc && (
        <DocumentPreview
          doc={previewDoc}
          docs={documents}
          onSelect={setPreviewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
      <div className="thin-scroll h-full overflow-y-auto">
        <div className="px-4 py-6 sm:px-6 2xl:px-10">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-ink-950">Documenten</h1>
            <p className="mt-1 text-sm text-ink-700">
              Bladeren door de mappen van de {kennisbank}, gesplitst in werkprocessen en
              zorginhoudelijke kennis. Klik op een titel om het document te bekijken.
            </p>
          </header>

          <div className="flex flex-col gap-5 lg:flex-row">
            <FolderTree
              folders={facets.folders}
              showCounts={folderCountsMeaningful}
              ring={ringFilter}
              documentType={typeFilter}
              onSelect={(r, t) => {
                setRingFilter(r);
                setTypeFilter(t);
              }}
            />
            <div className="min-w-0 flex-1">
              {/* Filter rail */}
              <section className="mb-4 space-y-3 rounded-xl border border-brand-100 bg-white p-4 shadow-card">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative w-full flex-1 sm:min-w-[260px] sm:w-auto">
                    <SearchIcon
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-700/50"
                    />
                    <input
                      type="search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Zoek in titel, auteur, instelling…"
                      className="w-full rounded-md border border-brand-200 bg-white pl-9 pr-3 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
                    />
                  </div>
                  <select
                    value={authorFilter}
                    onChange={(e) => setAuthorFilter(e.target.value)}
                    title={authorFilter || 'Alle auteurs'}
                    className="w-full truncate rounded-md border border-brand-200 bg-white px-3 py-1.5 text-sm focus:border-brand-400 focus:outline-none sm:w-auto sm:max-w-[200px]"
                  >
                    <option value="">Alle auteurs</option>
                    {facets.authors.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full rounded-md border border-brand-200 bg-white px-3 py-1.5 text-sm focus:border-brand-400 focus:outline-none sm:w-auto"
                  >
                    <option value="">Alle types</option>
                    {typeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                    <option value={UNTYPED_DOCUMENT_TYPE}>Zonder type</option>
                  </select>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-3 py-1.5 text-xs text-ink-800 transition-colors hover:bg-brand-50"
                    >
                      <XIcon size={12} /> Wis filters
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CANONICAL_SPECIALISATIONS.map((s) => {
                    const on = selectedSpecs.has(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleSpec(s)}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                          on
                            ? 'border-brand-700 bg-brand-700 text-white'
                            : 'border-brand-200 bg-white text-ink-800 hover:bg-brand-50'
                        }`}
                      >
                        {specialisationLabel(s)}
                      </button>
                    );
                  })}
                </div>
              </section>

              {error && (
                <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
              )}
              {saveError && (
                <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                  {saveError}
                </p>
              )}

              {/* Below xl (phones, tablets, zoomed-in desktops): stacked card list (avoids the cramped
              table). Read-only — admin inline editors live on the
              xl+ table view below. */}
              <div className="space-y-3 xl:hidden">
                {documents.map((d) => (
                  <article
                    key={d.filename}
                    className="rounded-xl border border-brand-100 bg-white p-4 shadow-card"
                  >
                    <button
                      type="button"
                      onClick={() => setPreviewDoc(d)}
                      title="Bekijk document"
                      className="flex w-full items-start gap-2 text-left"
                    >
                      <FileTextIcon
                        size={15}
                        className="mt-0.5 flex-shrink-0 text-brand-700/60"
                        aria-hidden="true"
                      />
                      <span className="text-sm font-semibold text-ink-900 hover:text-brand-700 hover:underline">
                        {d.documentTitle || d.filename}
                      </span>
                    </button>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {d.ring === 'ring0' && (
                        <span className="inline-flex items-center rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-800">
                          SUPPOHRT Intern
                        </span>
                      )}
                      {d.documentOnly && (
                        <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                          Alleen download
                        </span>
                      )}
                      {d.documentType && (
                        <span className="inline-flex items-center rounded-md border border-brand-200 bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-ink-800">
                          {d.documentType}
                        </span>
                      )}
                    </div>
                    <dl className="mt-3 space-y-1 text-xs">
                      <div className="flex gap-2">
                        <dt className="w-20 shrink-0 text-ink-900/50">Auteur</dt>
                        <dd className="text-ink-900/80">
                          {d.author || <span className="text-ink-900/30">—</span>}
                        </dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="w-20 shrink-0 text-ink-900/50">Geüpdatet</dt>
                        <dd className="text-ink-900/80">
                          {d.lastIngestedAt ? formatDate(d.lastIngestedAt) : '—'}
                        </dd>
                      </div>
                    </dl>
                    {d.specialisations && d.specialisations.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {d.specialisations.slice(0, 5).map((s) => (
                          <span
                            key={s}
                            className="rounded-full bg-mosterdgeel-50 px-2 py-0.5 text-[11px] font-medium text-mosterdgeel-600"
                          >
                            {specialisationLabel(s)}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 border-t border-brand-50 pt-2 text-[11px]">
                      <WijzigingsverzoekLink
                        context={{
                          surface: 'documenten',
                          filename: d.filename,
                          documentTitle: d.documentTitle ?? d.filename,
                          author: d.author ?? '',
                          specialisations: d.specialisations ?? [],
                        }}
                      />
                    </div>
                  </article>
                ))}
                {!loading && documents.length === 0 && (
                  <div className="rounded-xl border border-brand-100 bg-white px-4 py-10 text-center text-ink-900/60">
                    <p className="text-sm">
                      Geen documenten gevonden voor deze filters.{' '}
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-brand-700 hover:underline"
                        >
                          Wis filters
                        </button>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Desktop (xl+): the existing sortable table with admin inline edit.
              `overflow-visible` on the card so inline-edit dropdowns
              (Type, Specialisaties, Auteur) on the last row don't get
              clipped at the card's bottom edge. The cost is the thead's
              brand-50 background bleeding 1-2px past the rounded corners
              at the very top; accepted trade-off vs the popovers being
              cut off. The thead's own rounded clipping keeps it visually
              clean. */}
              <div className="hidden rounded-xl border border-brand-100 bg-white shadow-card xl:block">
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    {/* Title takes the remainder; the date column is fixed so
                    "Wijzigingsverzoek" never spills past the card edge. */}
                    <col />
                    <col className="w-[18%]" />
                    <col className="w-[12%]" />
                    <col className="w-[22%]" />
                    <col className="w-36" />
                  </colgroup>
                  <thead className="rounded-t-xl bg-brand-50/50 text-left text-[11px] uppercase tracking-wider text-ink-800/60">
                    <tr>
                      <SortableTh<SortColumn>
                        label="Titel"
                        column="title"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      />
                      <SortableTh<SortColumn>
                        label="Auteur"
                        column="author"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      />
                      <SortableTh<SortColumn>
                        label="Type"
                        column="documentType"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      />
                      <th className="px-4 py-3">Specialisaties</th>
                      <SortableTh<SortColumn>
                        label="Geüpdatet"
                        column="lastIngestedAt"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-50">
                    {documents.map((d) => (
                      <tr key={d.filename} className="align-top hover:bg-brand-50/40">
                        <td className="px-4 py-3 font-medium text-ink-900">
                          <div className="flex items-start gap-2">
                            {/* Title is the only clickable surface — the badge sits as a
                            sibling so its hover doesn't inherit the link styling
                            (underline) from the title button. */}
                            <button
                              type="button"
                              onClick={() => setPreviewDoc(d)}
                              title="Bekijk document"
                              className="inline-flex min-w-0 items-start gap-2 text-left text-ink-900 hover:text-brand-700 hover:underline"
                            >
                              <FileTextIcon
                                size={14}
                                className="mt-0.5 flex-shrink-0 text-brand-700/60"
                                aria-hidden="true"
                              />
                              <span className="break-words">{d.documentTitle || d.filename}</span>
                            </button>
                            {d.ring === 'ring0' && (
                              <span
                                title="SUPPOHRT-intern document — alleen zichtbaar voor SUPPOHRT-medewerkers."
                                className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-800"
                              >
                                SUPPOHRT Intern
                              </span>
                            )}
                            {d.documentOnly && (
                              <span
                                title="Alleen beschikbaar als download — de assistent doorzoekt dit document niet."
                                className="mt-0.5 inline-flex flex-shrink-0 items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                              >
                                Alleen download
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="break-words px-4 py-3 text-ink-900/70">
                          {canEdit ? (
                            <InlineAuthorCell
                              value={d.author}
                              saving={savingFilename === d.filename}
                              onSave={(v) => patchDocument(d.filename, { author: v })}
                            />
                          ) : (
                            d.author || <span className="text-ink-900/30">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink-900/70">
                          {canEdit ? (
                            <InlineDocumentTypeCell
                              value={d.documentType ?? ''}
                              saving={savingFilename === d.filename}
                              onSave={(v) =>
                                patchDocument(d.filename, {
                                  documentType: v === '' ? null : v,
                                } as Partial<DocumentRegistryEntry>)
                              }
                            />
                          ) : (
                            d.documentType || <span className="text-ink-900/30">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {canEdit ? (
                            <InlineSpecialisationsCell
                              value={d.specialisations ?? []}
                              saving={savingFilename === d.filename}
                              onSave={(v) => patchDocument(d.filename, { specialisations: v })}
                              maxItems={3}
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(d.specialisations ?? []).slice(0, 3).map((s) => (
                                <span
                                  key={s}
                                  className="rounded-full bg-mosterdgeel-50 px-2 py-0.5 text-[11px] font-medium text-mosterdgeel-600"
                                >
                                  {specialisationLabel(s)}
                                </span>
                              ))}
                              {(!d.specialisations || d.specialisations.length === 0) && (
                                <span className="text-ink-900/30">—</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink-900/70">
                          <div className="flex flex-col gap-0.5 whitespace-nowrap">
                            <span>{d.lastIngestedAt ? formatDate(d.lastIngestedAt) : '—'}</span>
                            {/* Inline practitioner-feedback channel — opens
                            modal → mailto. Available to every authenticated
                            user (admins use the inline-edit cells above
                            instead). */}
                            <WijzigingsverzoekLink
                              context={{
                                surface: 'documenten',
                                filename: d.filename,
                                documentTitle: d.documentTitle ?? d.filename,
                                author: d.author ?? '',
                                specialisations: d.specialisations ?? [],
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!loading && documents.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center text-ink-900/60">
                          <p className="text-sm">
                            Geen documenten gevonden voor deze filters.{' '}
                            {hasActiveFilters && (
                              <button
                                type="button"
                                onClick={clearFilters}
                                className="text-brand-700 hover:underline"
                              >
                                Wis filters
                              </button>
                            )}
                          </p>
                          <p className="mt-2 text-xs text-ink-900/50">
                            Of stel je vraag aan de{' '}
                            <Link
                              to={`/kennisagent${q ? `?q=${encodeURIComponent(q)}` : ''}`}
                              className="text-brand-700 hover:underline"
                            >
                              Kennisagent
                            </Link>
                            .
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer — server pages of PAGE_SIZE. Filters always
              run across the whole corpus; this just slices the rendered
              window. Hidden when there's nothing to paginate. */}
              <div className="mt-4 flex items-center justify-between text-[11px] text-ink-900/60">
                <span>
                  {total === 0 ? (
                    'Geen documenten'
                  ) : (
                    <>
                      {pageStart}–{pageEnd} van {total}
                      {hasActiveFilters ? ' (gefilterd)' : ''}
                    </>
                  )}
                </span>
                <div className="flex items-center gap-3">
                  {/* Page-size picker — defaults to 50; 100 / 200 for operators
                  who want to scroll instead of click through pages. Always
                  visible even when there's only one page so the user can
                  switch sizes without first having to land on a long
                  result set. */}
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-ink-900/60">Per pagina:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value) as PageSize)}
                      disabled={loading}
                      className="rounded-md border border-brand-200 bg-white px-1.5 py-0.5 text-xs text-ink-800 focus:border-brand-400 focus:outline-none disabled:opacity-40"
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                        className="rounded-md border border-brand-200 bg-white px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-40"
                      >
                        ‹ Vorige
                      </button>
                      <span className="text-xs">
                        Pagina {page} van {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || loading}
                        className="rounded-md border border-brand-200 bg-white px-2 py-1 text-xs text-ink-800 hover:bg-brand-50 disabled:opacity-40"
                      >
                        Volgende ›
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Externe bronnen (Ring 2/3) — bron-organisaties die Mila voeden,
              géén downloadbare documenten. Verbergt zich als er niets is. */}
          {externalSources.length > 0 && (
            <details className="mt-6 rounded-xl border border-petrol-100 bg-petrol-50/40 px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-ink-900 marker:text-petrol-700">
                Externe bronnen via Mila ({externalSources.length})
              </summary>
              <p className="mt-2 mb-3 text-xs text-ink-700">
                Geverifieerde externe websites die Mila gebruikt in haar antwoorden, met
                bronvermelding. Dit zijn geen downloadbare documenten zoals hierboven — klik op een
                bron om de website te openen.
              </p>
              <div className="flex flex-wrap gap-2">
                {externalSources.map((s) => {
                  const cls = `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                    s.ring === 2
                      ? 'border-petrol-200 bg-white text-petrol-700 hover:bg-petrol-50'
                      : 'border-brand-200 bg-white text-ink-800 hover:bg-brand-50'
                  }`;
                  const body = (
                    <>
                      <span className="font-medium">{s.label}</span>
                      <span className="text-ink-900/40">· {s.pageCount}</span>
                      {s.homepage && <ExternalLinkIcon size={11} className="opacity-60" />}
                    </>
                  );
                  return s.homepage ? (
                    <a
                      key={`${s.ring}:${s.id}`}
                      href={s.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${s.label} — Ring ${s.ring}, ${s.pageCount} pagina's in de kennisbank`}
                      className={cls}
                    >
                      {body}
                    </a>
                  ) : (
                    <span key={`${s.ring}:${s.id}`} className={cls}>
                      {body}
                    </span>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      </div>
    </Layout>
  );
}

/**
 * Mappenboom à la SharePoint: twee bibliotheken (werkprocessen /
 * zorginhoudelijke kennis) met documenttypes als submappen. Puur een
 * andere ingang op dezelfde filters — een klik zet ring + type, de
 * bestaande zoek-, auteur- en specialisatiefilters blijven daarbovenop
 * werken. Bibliotheken zonder documenten renderen niet (zo verdwijnt
 * Werkprocessen vanzelf voor externe gebruikers).
 */
function FolderTree({
  folders,
  ring,
  documentType,
  showCounts,
  onSelect,
}: {
  folders: FolderFacet[];
  ring: '' | 'ring0' | 'ring1';
  documentType: string;
  showCounts: boolean;
  onSelect: (ring: '' | 'ring0' | 'ring1', documentType: string) => void;
}): JSX.Element {
  const libraries = LIBRARIES.map((lib) => {
    const own = folders.filter((f) => f.ring === lib.ring);
    return {
      ...lib,
      total: own.reduce((n, f) => n + f.count, 0),
      // Documenten zonder type onderaan als "Overig".
      subfolders: [...own.filter((f) => f.documentType), ...own.filter((f) => !f.documentType)],
    };
  }).filter((lib) => lib.total > 0);

  if (libraries.length === 0) return <></>;

  return (
    <aside className="w-full flex-shrink-0 lg:w-60">
      <nav className="rounded-xl border border-brand-100 bg-white p-3 shadow-card">
        <button
          type="button"
          onClick={() => onSelect('', '')}
          className={`mb-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
            ring === '' && documentType === ''
              ? 'bg-brand-700 text-white'
              : 'text-ink-900 hover:bg-brand-50'
          }`}
        >
          <FolderIcon size={14} className="flex-shrink-0 opacity-70" aria-hidden="true" />
          <span className="font-medium">Alle documenten</span>
        </button>
        {libraries.map((lib) => (
          <div key={lib.ring} className="mb-1">
            <button
              type="button"
              onClick={() => onSelect(lib.ring, '')}
              title={lib.hint}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                ring === lib.ring && documentType === ''
                  ? 'bg-brand-700 text-white'
                  : 'text-ink-900 hover:bg-brand-50'
              }`}
            >
              <FolderIcon size={14} className="flex-shrink-0 opacity-70" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate font-medium">{lib.label}</span>
              {showCounts && <span className="text-[11px] opacity-60">{lib.total}</span>}
            </button>
            <ul className="ml-3 border-l border-brand-100 pl-2">
              {lib.subfolders.map((f) => {
                // Documenten zonder type reizen als sentinel over de wire; een
                // lege string zou op de server als "geen typefilter" landen en
                // de hele ring tonen onder een map die drie documenten belooft.
                const value = f.documentType || UNTYPED_DOCUMENT_TYPE;
                const on = ring === lib.ring && documentType === value;
                return (
                  <li key={value}>
                    <button
                      type="button"
                      onClick={() => onSelect(lib.ring, value)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors ${
                        on
                          ? 'bg-brand-50 font-medium text-brand-700'
                          : 'text-ink-800 hover:bg-brand-50'
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">{f.documentType || 'Overig'}</span>
                      {showCounts && <span className="opacity-50">{f.count}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ----- Inline editing cells (admin-only) ------------------------------------

/**
 * Click-to-edit author cell. Idle state is plain text identical to the
 * non-admin render so the table doesn't look busy. Click → input + save
 * on Enter / blur, cancel on Escape. Empty value rejected (server enforces
 * the same invariant — author is required on chunks).
 */
function InlineAuthorCell({
  value,
  saving,
  onSave,
}: {
  value: string;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      // Defer focus so the input is mounted.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing, value]);

  const commit = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      return;
    }
    await onSave(trimmed);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Klik om de auteur aan te passen"
        className="w-full rounded-md px-1.5 py-0.5 text-left hover:bg-brand-50"
      >
        {value || <span className="text-ink-900/30">—</span>}
      </button>
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
        } else if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
      aria-label="Auteur bewerken"
      className="w-full rounded-md border border-brand-200 bg-white px-2 py-0.5 text-sm text-ink-900 outline-none focus:border-brand-500"
    />
  );
}

/**
 * Single-select dropdown for the canonical documentType vocabulary.
 * Idle state is plain text (or — for empty); click → popover with the
 * 10 canonical genres + a "Wissen" option to clear back to none.
 */
function InlineDocumentTypeCell({
  value,
  saving,
  onSave,
}: {
  value: string;
  saving: boolean;
  onSave: (next: string) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = async (next: string): Promise<void> => {
    setOpen(false);
    if (next === value) return;
    await onSave(next);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        title="Klik om documenttype aan te passen"
        className="w-full rounded-md px-1.5 py-0.5 text-left hover:bg-brand-50 disabled:opacity-50"
      >
        {value || <span className="text-ink-900/30">—</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 max-h-60 w-44 overflow-y-auto rounded-md border border-brand-200 bg-white p-1 shadow-lg">
          {CANONICAL_DOCUMENT_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => void choose(t)}
              className={`block w-full rounded px-2 py-1 text-left text-xs hover:bg-brand-50 ${
                t === value ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-900'
              }`}
            >
              {t}
            </button>
          ))}
          {value && (
            <>
              <div className="my-1 border-t border-brand-100" />
              <button
                type="button"
                onClick={() => void choose('')}
                className="block w-full rounded px-2 py-1 text-left text-xs text-ink-700 hover:bg-red-50 hover:text-red-700"
              >
                Wissen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select chip editor for the canonical specialisation vocabulary.
 * Existing chips show with an X to remove. A "+ tag" button reveals the
 * remaining canonical options as an inline picker. Saves on every chip
 * add/remove (small list, no commit-on-blur ceremony needed).
 */
